import type { EditorView } from "@codemirror/view";
import {
  cursorCharLeft,
  cursorCharRight,
  cursorLineDown,
  cursorLineUp,
  indentLess,
  indentMore,
  redo,
  undo,
} from "@codemirror/commands";
import { icon } from "./icons";
import { copyToClipboard, pasteFromClipboard } from "./clipboard";

const SYMBOL_KEYS = ["{", "}", "(", ")", "[", "]", '"', "'", ";", ":", "=", "<", ">", "/", "\\", "|", "_", "-", "+"];

function insertAtCursor(view: EditorView, text: string) {
  view.dispatch(view.state.replaceSelection(text));
  view.focus();
}

function clearAll(view: EditorView) {
  if (!window.confirm("Clear all content in this file?")) return;
  view.dispatch({ changes: { from: 0, to: view.state.doc.length, insert: "" } });
  view.focus();
}

async function copyAll(view: EditorView) {
  await copyToClipboard(view.state.doc.toString());
}

async function pasteAtCursor(view: EditorView) {
  const text = await pasteFromClipboard();
  if (text === null) {
    window.alert("Clipboard paste isn't available here (needs HTTPS or localhost). Long-press in the editor to paste instead.");
    return;
  }
  insertAtCursor(view, text);
}

function button(label: string, innerHtml: string): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.setAttribute("aria-label", label);
  btn.className =
    "flex h-9 min-w-9 shrink-0 items-center justify-center rounded-md bg-panel-muted px-2.5 font-mono text-sm text-foreground active:bg-white/20";
  btn.innerHTML = innerHtml;
  return btn;
}

export function renderToolbar(container: HTMLElement, getView: () => EditorView | null) {
  container.innerHTML = "";
  const run = (fn: (view: EditorView) => void) => {
    const view = getView();
    if (view) fn(view);
  };

  const actions: Array<[string, string, (view: EditorView) => void]> = [
    ["Dismiss keyboard", icon("keyboard"), (v) => v.contentDOM.blur()],
    ["Undo", icon("undo2"), undo as (v: EditorView) => void],
    ["Redo", icon("redo2"), redo as (v: EditorView) => void],
    ["Unindent", icon("chevronsLeft"), indentLess as (v: EditorView) => void],
    ["Indent / Tab", icon("chevronsRight"), indentMore as (v: EditorView) => void],
    ["Move left", icon("arrowLeft"), cursorCharLeft as (v: EditorView) => void],
    ["Move right", icon("arrowRight"), cursorCharRight as (v: EditorView) => void],
    ["Move up", icon("arrowUp"), cursorLineUp as (v: EditorView) => void],
    ["Move down", icon("arrowDown"), cursorLineDown as (v: EditorView) => void],
  ];

  const clipboardActions: Array<[string, string, (view: EditorView) => void]> = [
    ["Clear all", icon("eraser"), clearAll],
    ["Copy all", icon("copy"), copyAll],
    ["Paste", icon("clipboardPaste"), pasteAtCursor],
  ];

  const addDivider = () => {
    const divider = document.createElement("div");
    divider.className = "mx-0.5 h-6 w-px shrink-0 bg-border";
    container.appendChild(divider);
  };

  for (const [label, html, fn] of actions) {
    const btn = button(label, html);
    btn.addEventListener("pointerdown", (e) => {
      e.preventDefault();
      run(fn);
    });
    container.appendChild(btn);
  }

  addDivider();

  for (const [label, html, fn] of clipboardActions) {
    const btn = button(label, html);
    btn.addEventListener("pointerdown", (e) => {
      e.preventDefault();
      run(fn);
    });
    container.appendChild(btn);
  }

  addDivider();

  for (const key of SYMBOL_KEYS) {
    const btn = button(key, key);
    btn.addEventListener("pointerdown", (e) => {
      e.preventDefault();
      run((v) => insertAtCursor(v, key));
    });
    container.appendChild(btn);
  }
}
